import {PLACE_ICONS, DEFAULT_PLACE_ICON} from '../styles';
import Autosuggest, {AutosuggestProps} from 'react-autosuggest';
import CircularProgress from '@material-ui/core/CircularProgress';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import React, {useState, useCallback, useRef} from 'react';
import theme from '../theme';
import {memoize} from 'lodash';
import trackAnalytics from '../utils/gaia/trackAnalytics';
import fetchJSON from '../utils/fetchJSON';
import qs from 'qs';
import useGetSyncPromise from '../utils/react/useGetSyncPromise';
import parseCoordinates from '../utils/parseCoordinates';
import {searchZIndex, searchSuggestionsZIndex} from '../utils/gaia/zIndicies';

export interface SearchBoxProps extends
    Omit<Partial<AutosuggestProps<SearchResult>>, 'inputProps'>
{
    onSearchResultSelected?: (
        event: React.FormEvent<any>,
        data: Autosuggest.SuggestionSelectedEventData<SearchResult>,
        url: string,
    ) => void;
    center: mapboxgl.LngLat | undefined;
    inputProps: Partial<AutosuggestProps<SearchResult>['inputProps']>;
}

const fetchSuggestions = memoize(async (
    query: string,
    center: mapboxgl.LngLat | undefined,
    pressed_enter: boolean,
): Promise<SearchResult[]> => {

    if (query.length < 3) return [];

    const url = '/api/trip/autocomplete/?' + qs.stringify({
        query,
        pressed_enter,
        ...center && {
            lat: center.lat.toFixed(3),
            lon: center.lng.toFixed(3),
        }
    });

    const {results} = await fetchJSON(url);

    trackAnalytics('Fetched Suggestions', {
        category: 'Hike',
        resultCount: results.length,
        query,
    });

    return results;

}, (...args) => JSON.stringify(args));

function getSearchResultUrl(searchResult: SearchResult) {
    if (searchResult.permalink) {
        return searchResult.permalink;
    } else if (searchResult.type === 'known_route') {
        return `/hike/${searchResult.known_route_id}`;
    } else if (searchResult.centroid) {
        return (
            '/hike/nearby/' +
            `?lat=${searchResult.centroid[1]}` +
            `&lon=${searchResult.centroid[0]}` +
            `&name=${searchResult.title}`
        );
    } else {
        return '';
    }
}

export default function SearchBox({
    onSearchResultSelected,
    center,
    ...props
}: SearchBoxProps) {

    const getSyncPromise = useGetSyncPromise();
    const [value, setValue] = useState('');
    const [coordinateSuggestions, setCoordinateSuggestions] = useState<SearchResult[]>([]);
    const [suggestionsPromise, setSuggestionsPromise] = useState<Promise<SearchResult[]>>();

    const clearSuggestions = useCallback(() => {
        setValue('');
        setSuggestionsPromise(undefined);
        setCoordinateSuggestions([]);
    }, []);

    const [suggestions,, isSuggestionsLoaded] = suggestionsPromise ?
        getSyncPromise(suggestionsPromise) :
        [undefined, undefined, true];

    const ref = useRef<any>();

    return <div style={{display: 'flex', alignItems: 'center'}} className='mapboxgl-ctrl'>
        <Autosuggest
            suggestions={[
                ...coordinateSuggestions,
                ...suggestions || []
            ]}
            onSuggestionsFetchRequested={(params: Autosuggest.SuggestionsFetchRequestedParams) => {
                if (props.onSuggestionsFetchRequested) props.onSuggestionsFetchRequested(params);
                setCoordinateSuggestions(getCoordinateSuggestions(params.value));
                setSuggestionsPromise(fetchSuggestions(params.value, center, false));
            }}
            getSuggestionValue={(suggestion: SearchResult) => suggestion.title}
            renderSuggestion={renderSuggestion}
            renderSuggestionsContainer={renderSuggestionsContainer}
            onSuggestionSelected={(
                event: React.FormEvent<any>,
                data: Autosuggest.SuggestionSelectedEventData<SearchResult>
            ) => {
                const url = getSearchResultUrl(data.suggestion);

                trackAnalytics('Chose Suggestion', {
                    category: 'Hike',
                    url,
                    query: value,
                    name: data.suggestion.title
                });

                if (onSearchResultSelected) {
                    onSearchResultSelected(event, data, url);
                } else {
                    window.location.href = url;
                }

                clearSuggestions();
            }}
            onSuggestionsClearRequested={clearSuggestions}
            {...props}
            ref={ref}
            inputProps={{
                onChange: (event, {newValue}) => setValue(newValue),
                onKeyDown: event => {
                    if (event.key === 'Enter') {
                        if (ref.current && !ref.current.getHighlightedSuggestion()) {
                            setSuggestionsPromise(fetchSuggestions(value, center, true));
                            ref.current.setState({isCollapsed: false});
                        }
                    }
                },
                onFocus: () => {
                    trackAnalytics('Selected Search Box', {category: 'Hike'});
                },
                value: value,
                placeholder: 'Search',
                style: {
                    borderWidth: 2,
                    borderColor: 'rgba(0, 0, 0, 0.1)',
                    borderRadius: theme.shape.borderRadius,
                    fontFamily: theme.typography.fontFamily,
                    fontSize: 15,
                    maxWidth: 360,
                    minWidth: 240,
                    paddingBottom: theme.spacing(1),
                    paddingLeft: theme.spacing(1.5),
                    paddingRight: theme.spacing(1.5),
                    paddingTop: theme.spacing(1),
                    position: 'relative',
                    width: '33.3333%',
                    zIndex: searchZIndex
                },
                autoComplete: 'off',
                spellCheck: false,
                autoCorrect: 'off',
                autoCapitalize: 'off',
                ...props.inputProps
            }}
            theme={{
                suggestionsList: {listStyleType: 'none', padding: 0, margin: 0},
                ...props.theme
            }}
        />
        {!isSuggestionsLoaded && <CircularProgress
            color="primary"
            style={{marginLeft: theme.spacing(1)}}
            size={20}
            thickness={7}
        />}
    </div>;
}

function getCoordinateSuggestions(query: string): SearchResult[] {
    const coordinates = parseCoordinates(query);
    if (!coordinates) return [];

    const xDirection = coordinates[1] > 0 ? 'E' : 'W';
    const yDirection = coordinates[0] > 0 ? 'N' : 'S';

    const x = `${1 * (Math.abs(coordinates[1]).toFixed(6) as any)}°${xDirection}`;
    const y = `${1 * (Math.abs(coordinates[0]).toFixed(6) as any)}°${yDirection}`;

    return [{
        bounds: null,
        centroid: [coordinates[1], coordinates[0]],
        id: null,
        known_route_id: null,
        permalink_api: null,
        permalink: `/hike/nearby/?lat=${coordinates[1]}&lon=${coordinates[0]}&name=${query}`,
        subtitle: 'Coordinate',
        subtype: 'coordinate',
        title: `${y} ${x}`,
        type: 'coordinate',
    }];
}

function renderSuggestion(
    searchResult: SearchResult,
    {isHighlighted}: Autosuggest.RenderSuggestionParams
) {
    const {title, subtitle, subtype} = searchResult;
    return <MenuItem selected={isHighlighted} component='div'>
        <span style={{color: theme.palette.main}}>
            {PLACE_ICONS[subtype as keyof typeof PLACE_ICONS] || DEFAULT_PLACE_ICON}
        </span>
        <ListItemText primary={title} secondary={subtitle} />
    </MenuItem>;
}

function renderSuggestionsContainer(options: Autosuggest.RenderSuggestionsContainerParams) {
    return <Paper {...options.containerProps} style={{
        marginTop: -8,
        paddingTop: 8,
        position: 'absolute',
        zIndex: searchSuggestionsZIndex,
    }}>
        {options.children}
    </Paper>;
}
